"use client";

import { createColumnHelper } from "@tanstack/react-table";
import { Text } from "rizzui";
import AvatarCard from "@core/ui/avatar-card";
import DateCell from "@core/ui/date-cell";
import TableRowActionGroup from "../../table-row-action-group";
import { routes } from "../../../../config/routes";
import { MoreActions } from "../../../../components/table-utils/user-more-actions";
import { User } from "packages/app/src/types";
import { suspendUser } from "../../../api/suspend-user";

const columnHelper = createColumnHelper<User>();

export const AllUsersListColumn = [
  columnHelper.accessor("userId", {
    header: "User ID",
    size: 150,
    cell: (info) => {
      const id = info.getValue();
      return <Text className="text-sm">{id ?? "-"}</Text>;
    },
  }),

  columnHelper.display({
    id: "fullName",
    header: "Name",
    size: 300,
    cell: ({ row }) => {
      const u = row.original;
      const name =
        `${u.firstName ?? ""}${u.middleName ? ` ${u.middleName}` : ""} ${u.lastName ?? ""}`.trim();
      return (
        <AvatarCard
          src={u.profileImage ?? ""}
          name={name || "-"}
          description={u.accountType ?? "-"}
        />
      );
    },
  }),

  columnHelper.accessor("email", {
    header: "Email",
    size: 250,
    cell: (info) => {
      const email = info.getValue() ?? "";
      return <Text className="text-sm">{email.toLowerCase()}</Text>;
    },
    filterFn: (row, id, filterValue) => {
      const email = row.getValue<string>(id) ?? "";
      return email.toLowerCase().includes(String(filterValue).toLowerCase());
    },
  }),

  columnHelper.accessor("phoneNumber", {
    header: "Phone",
    size: 180,
    cell: (info) => {
      const num = info.getValue();
      return <Text className="text-sm">{num != null ? `+${num}` : "-"}</Text>;
    },
  }),

  columnHelper.accessor("kycVerified", {
    header: "KYC Status",
    size: 140,
    filterFn: (row, id, filterValue) =>
      !filterValue || row.getValue<string>(id) === filterValue,
    cell: (info) => {
      const status = info.getValue();
      const color =
        status === "Success"
          ? "text-green-600"
          : status === "Failed"
            ? "text-red-600"
            : "text-gray-600";
      return (
        <Text className={`text-sm font-medium ${color}`}>{status ?? "-"}</Text>
      );
    },
  }),

  columnHelper.accessor("verified", {
    header: "Email Verified",
    size: 130,
    cell: (info) => <Text>{info.getValue() ? "Yes" : "No"}</Text>,
    filterFn: (row, id, filterValue) => {
      if (!filterValue) return true;
      return row.getValue<boolean>(id) === (filterValue === "true");
    },
  }),

  columnHelper.accessor("isSuspended", {
    header: "Suspended",
    size: 130,
    cell: (info) => <Text>{info.getValue() ? "Yes" : "No"}</Text>,
    filterFn: (row, id, filterValue) => {
      if (!filterValue) return true;
      return row.getValue<boolean>(id) === (filterValue === "true");
    },
  }),

  columnHelper.accessor("createdAt", {
    header: "Joined",
    size: 180,
    cell: ({ row }) => {
      const dateVal = row.original.createdAt;
      const date = dateVal ? new Date(dateVal) : null;
      return date ? <DateCell date={date} /> : <Text>-</Text>;
    },
  }),

  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: ({ row, table: { options } }) => (
      <MoreActions
        id={row.original.userId}
        currentStatus={row.original.kycVerified}
        currentSuspendStatus={row.original.isSuspended!}
        onSuspend={async (reason: string) => {
          try {
            await suspendUser(row.original.isSuspended!, row.original.userId, reason);
            options.meta?.updateRow?.(row.index, { isSuspended: !row.original.isSuspended });
          } catch (error) {
            console.log('Error suspending user', error)
          }
        }}
        onStatusUpdated={(
          newStatus: "Success" | "Pending" | "Failed" | "YetToStart" | "Issued"
        ) => options.meta?.updateRow?.(row.index, { kycVerified: newStatus })}
      />
    ),
  }),
];
